import React from 'react'

import { Box, CounterBadge, Icon, Text, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { CONDITIONS_VALUES } from 'modules/core/components/MultiFilters/FilterItem/useFilterItem'
import type LogicOperatorForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import type { FilterOption } from 'modules/core/components/MultiFilters/types'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import type { Attachment } from 'modules/core/components/TetherComponent/types'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import FilterPanel from 'modules/surveys/components/MultiFilters'
import type { AnalyticsFilterForm } from 'modules/surveys/hooks/useAnalyticsMultiFilters'

import FilterContent, { Field } from './Content'
import { Action, CustomTooltip, DEFAULT_OPTIONS, State, constants } from './filterDefaults'

const initialState = {
  advanced: false,
  fieldSelected: '',
}

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case constants.RESET_FIELDS:
      return initialState

    case constants.CHANGE_SELECTED_FIELD:
      return {
        ...state,
        fieldSelected: action.field || '',
      }

    case constants.USE_ADVANCED_FILTER:
      return {
        ...state,
        advanced: true,
      }

    default:
      throw new Error(`No such type: ${action.type}`)
  }
}

export interface Props {
  label: string
  fields: readonly FilterOption[]
  filters: MultiFilterForm[]
  logicOperator: LogicOperatorForm
  onAddFilter: () => void
  onRemoveFilter: (index: number) => void
  onApplyFilters: (isActive: boolean, form?: MultiFilterForm[]) => void
  onCancel: () => void
  emptyStateLabel?: string
  addFilterLabel?: string
  submitLabel?: string
  attachment?: Attachment
  filterKey: AnalyticsFilterForm
  filterDependencies?: string[]
}

const Filters = ({
  label,
  emptyStateLabel,
  fields,
  filters,
  logicOperator,
  onAddFilter,
  onRemoveFilter,
  onApplyFilters,
  onCancel,
  filterKey,
  filterDependencies = [],
}: Props) => {
  const [state, dispatch] = React.useReducer(reducer, initialState)
  const { advanced, fieldSelected } = state
  const [open, toggle] = useToggle(false)

  const calculateCount = () => {
    if (!filterKey) return filters.length

    const filterDefaultValues = filterKey.default

    if (!filterKey.isActive) {
      return constants.RESET_FILTER_COUNT
    } else if (!filterDefaultValues?.length) {
      return filters.length
    } else {
      return constants.DEFAULT_FILTER_COUNT
    }
  }

  const count = calculateCount()

  const DEFAULT_TRANSLATIONS = {
    team: i18n.t('filters.multi_filters.team'),
    location: i18n.t('filters.multi_filters.location'),
  }

  const resetFields = () =>
    dispatch({
      type: constants.RESET_FIELDS,
    })

  useEffect(() => {
    const defaultFilter = filterKey?.default
    const defaultValues = defaultFilter?.[0]

    if (!defaultFilter?.length || !defaultValues) {
      onApplyFilters(false)

      return
    }

    onApplyFilters(true, [new MultiFilterForm(defaultValues)])
  }, [...filterDependencies]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnApplyFilters = () => {
    toggle(false)()
    onApplyFilters(true)
    resetFields()
  }

  const handleOnCancel = () => {
    onCancel()
    resetFields()
  }

  const resetFilters = () => applyResetFieldOption()

  const changeSelectedField = (field: string) => {
    dispatch({
      type: constants.CHANGE_SELECTED_FIELD,
      field,
    })
  }

  const applyFieldOption = async (field: string, value: string) => {
    const filterForm = new MultiFilterForm({
      condition: CONDITIONS_VALUES.isEqual,
      field,
      value,
    })
    toggle(false)()
    onApplyFilters(true, [filterForm])
    resetFields()
  }

  const applyResetFieldOption = async () => {
    toggle(false)()
    onApplyFilters(false)
    resetFields()
  }

  const getSelectedFieldOptions = () => {
    const field = fields.find((field) => field.value === fieldSelected)

    const options = field?.options?.map((option) => {
      return {
        title: option.label,
        selected: isOptionSelected(option.value, 'value', fieldSelected),
        onClick: () => applyFieldOption(fieldSelected, option.value),
      } as Field
    })

    return options || []
  }

  const getFieldOptions = () => {
    const options: Field[] = [
      {
        title: DEFAULT_TRANSLATIONS.team,
        selected: isOptionSelected(DEFAULT_OPTIONS.team),
        onClick: () => changeSelectedField(DEFAULT_OPTIONS.team),
      },
      {
        title: DEFAULT_TRANSLATIONS.location,
        selected: isOptionSelected(DEFAULT_OPTIONS.location),
        onClick: () => changeSelectedField(DEFAULT_OPTIONS.location),
      },
      // {
      //   title: i18n.t('filters.default_filters.advanced_filter'),
      //   selected: isAdvancedFilterSelected(),
      //   onClick: () => dispatch({ type: constants.USE_ADVANCED_FILTER })
      // }
    ]

    return options
  }

  const isAnyFilterSelected = () => filterKey.isActive
  const isDefaultFilterSelected = () => isAnyFilterSelected() && filterKey?.default?.length > 0
  const isAdvancedFilterSelected = () => isAnyFilterSelected() && !isDefaultFilterSelected()

  const getSuboptionSelected = () => {
    const selectedField = fields?.find((item) => item.value === filterKey.default[0]?.field)

    return selectedField?.options?.find((item) => item.value === filterKey.default[0]?.value)
  }

  const labelTooltip = () => {
    if (isDefaultFilterSelected()) {
      const translations = DEFAULT_TRANSLATIONS as { [key: string]: string }

      return `${translations[filterKey.default[0]?.field]}: ${getSuboptionSelected()?.label}`
    }

    if (isAdvancedFilterSelected()) return i18n.t('filters.default_filters.advanced_filters')

    return ''
  }

  const isOptionSelected = (option: string, key = 'field', parentField = '') => {
    if (!isDefaultFilterSelected()) return false

    const selectedValue = filterKey.default[0] || {}
    const optionMatched = option === selectedValue[key]

    if (parentField) return optionMatched && parentField === selectedValue.field

    return optionMatched
  }

  return (
    <PopoverMenu type="list" onClick={toggle(!open)} open={open}>
      <PopoverMenu.Trigger>
        <Box
          onClick={toggle(true)}
          flexDirection="row"
          alignItems="center"
          width="s200"
          justifyContent="spaceBetween"
          background="grey000White"
          border={{
            all: {
              width: 's2',
              style: 'solid',
              color: 'grey400',
            },
          }}
          borderRadius={{
            all: 'abs008',
          }}
          padding="s8"
          cursor="pointer"
        >
          <Box flexDirection="row" gap="s6">
            <CustomTooltip label={labelTooltip()} enabled={isAnyFilterSelected()}>
              <CounterBadge count={count} noCountIcon="filter" />
            </CustomTooltip>
            <Text size="300" color="grey1000">
              {label}
            </Text>
          </Box>
          <Icon.Small icon={open ? 'caret-up' : 'caret-down'} />
        </Box>
      </PopoverMenu.Trigger>

      <PopoverMenu.Content side="bottom" align="center" width={advanced ? 's600' : 's300'}>
        {!advanced && !fieldSelected && (
          <FilterContent
            header={i18n.t('filters.default_filters.filter_by')}
            reset={i18n.t('filters.default_filters.reset_filters')}
            getFields={getFieldOptions}
            onClickHeader={resetFilters}
          />
        )}
        {!!fieldSelected && (
          <FilterContent
            header={DEFAULT_TRANSLATIONS[fieldSelected as keyof typeof DEFAULT_TRANSLATIONS]}
            onClickHeader={resetFields}
            icon="chevron-left"
            getFields={getSelectedFieldOptions}
          />
        )}
        {advanced && (
          <FilterPanel
            emptyStateLabel={emptyStateLabel}
            fields={fields}
            filters={filters}
            logicOperator={logicOperator}
            onAddFilter={onAddFilter}
            onRemoveFilter={onRemoveFilter}
            onApplyFilters={handleOnApplyFilters}
            onCancel={handleOnCancel}
            cancelLabel={i18n.t('filters.default_filters.return_to_list')}
          />
        )}
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

export default observer(Filters)
